import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Auto-retry on network errors (server waking up — can take 15-30s)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // If 401 unauthorized, token is expired — force re-login
    if (error.response?.status === 401 && !config.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    if (!config || config._retryCount >= 5) return Promise.reject(error);
    
    const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';
    const isServerError = error.response?.status >= 500;
    
    if (isNetworkError || isServerError) {
      config._retryCount = (config._retryCount || 0) + 1;
      await new Promise(r => setTimeout(r, 3000));
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  employeeRegister: (data) => api.post('/auth/employee-register', data),
  employeeLogin: (data) => api.post('/auth/employee-login', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const expenseRequestsAPI = {
  getAll: () => api.get('/expense-requests'),
  create: (data) => api.post('/expense-requests', data),
  uploadReceipt: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/expense-requests/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  approve: (id, data) => api.put(`/expense-requests/${id}/approve`, data),
  reject: (id, data) => api.put(`/expense-requests/${id}/reject`, data),
  getMeta: () => api.get('/expense-requests/meta'),
  myRole: () => api.get('/expense-requests/my-role'),
  accountsQueue: (seenAt) => api.get('/expense-requests/accounts-queue', { params: seenAt ? { seen_at: seenAt } : {} }),
  getAccountsContact: () => api.get('/settings/accounts-contact'),
  setAccountsContact: (data) => api.put('/settings/accounts-contact', data),
  setAdvanceType: (id, advance_type) => api.put(`/expense-requests/${id}/advance-type`, { advance_type }),
  setPayment: (id, data) => api.put(`/expense-requests/${id}/payment`, data),
  verify: (id, data) => api.put(`/expense-requests/${id}/verify`, data),
  update: (id, data) => api.put(`/expense-requests/${id}`, data),
  remove: (id) => api.delete(`/expense-requests/${id}`),
  transcribe: (blob, language, context) => {
    const formData = new FormData();
    const ext = (blob.type.split('/')[1] || 'webm').split(';')[0];
    formData.append('file', blob, `note.${ext}`);
    if (language) formData.append('language', language);
    if (context) formData.append('context', context);
    return api.post('/voice/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getCustomCategories: () => api.get('/expense-categories'),
};

export const cranesAPI = {
  getAll: () => api.get('/cranes'),
  getById: (id) => api.get(`/cranes/${id}`),
  create: (data) => api.post('/cranes', data),
  update: (id, data) => api.put(`/cranes/${id}`, data),
  delete: (id) => api.delete(`/cranes/${id}`),
  getMaintenanceHistory: (id) => api.get(`/cranes/${id}/maintenance-history`),
  getServiceAlerts: () => api.get('/cranes/service-alerts'),
  // Deployment History
  getDeploymentHistory: (id) => api.get(`/cranes/${id}/deployment-history`),
  addDeploymentRecord: (id, data) => api.post(`/cranes/${id}/deployment-history`, data),
  updateDeploymentRecord: (id, recordId, data) => api.put(`/cranes/${id}/deployment-history/${recordId}`, data),
  deleteDeploymentRecord: (id, recordId) => api.delete(`/cranes/${id}/deployment-history/${recordId}`),
  uploadDeploymentExcel: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cranes/${id}/deployment-history/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDeploymentTemplate: (id) => api.get(`/cranes/${id}/deployment-history/template`, { responseType: 'blob' }),
};

export const expensesAPI = {
  getAll: (craneId) => api.get('/expenses', { params: { crane_id: craneId } }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

export const revenueAPI = {
  getAll: (craneId) => api.get('/revenue', { params: { crane_id: craneId } }),
  create: (data) => api.post('/revenue', data),
  update: (id, data) => api.put(`/revenue/${id}`, data),
  delete: (id) => api.delete(`/revenue/${id}`),
  import: (file, siteId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_id', siteId);
    return api.post('/revenue/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const maintenanceAPI = {
  getAll: (craneId) => api.get('/maintenance', { params: { crane_id: craneId } }),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
};

export const consumablesAPI = {
  getAll: (craneId) => api.get('/consumables', { params: { crane_id: craneId } }),
  create: (data) => api.post('/consumables', data),
  update: (id, data) => api.put(`/consumables/${id}`, data),
  delete: (id) => api.delete(`/consumables/${id}`),
  exportExcel: (month) => api.get('/consumables/export', { params: { month: month || undefined }, responseType: 'blob' }),
  uploadBill: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/consumables/${id}/bill`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getBill: (path) => api.get(`/consumables/bill/${path}`, { responseType: 'blob' }),
  deleteBill: (id, path) => api.delete(`/consumables/${id}/bill`, { params: { path } }),
};

export const clientsAPI = {
  getAll: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

export const sitesAPI = {
  getAll: () => api.get('/sites'),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  patch: (id, data) => api.patch(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
  getSummary: (id) => api.get(`/sites/${id}/summary`),
  uploadWorkOrder: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/sites/${id}/upload-work-order`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getWorkOrder: (id, params) => api.get(`/sites/${id}/work-order`, { responseType: 'blob', params }),
};

export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getCategorySummary: (params) => api.get('/dashboard/category-summary', { params }),
};

export const analyticsAPI = {
  getCranePerformance: () => api.get('/analytics/crane-performance'),
  getClientPerformance: () => api.get('/analytics/client-performance'),
  getSitePerformance: () => api.get('/analytics/site-performance'),
  getDebtors: () => api.get('/analytics/debtors'),
  getSiteOS: (siteId) => api.get(`/analytics/site-os/${siteId}`),
  exportSiteOS: (siteId) => api.get(`/export/site-os/${siteId}`, { responseType: 'blob' }),
  exportSiteOSPdf: (siteId) => api.get(`/export/site-os-pdf/${siteId}`, { responseType: 'blob' }),
  // New detailed analytics
  getSiteExpenseBreakdown: () => api.get('/analytics/site-expense-breakdown'),
  getCraneExpenseBreakdown: () => api.get('/analytics/crane-expense-breakdown'),
  getCraneDeploymentOverview: () => api.get('/analytics/crane-deployment-overview'),
  // Deployment History
  getAllDeploymentHistory: () => api.get('/analytics/all-deployment-history'),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const billsAPI = {
  getAll: (siteId) => api.get('/bills', { params: { site_id: siteId } }),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  delete: (id) => api.delete(`/bills/${id}`),
};

export const employeesAPI = {
  getAll: (siteId) => api.get('/employees', { params: { site_id: siteId } }),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  updateNotes: (id, notes) => api.put(`/employees/${id}/notes`, { notes }),
  getLeaves: (id) => api.get(`/employees/${id}/leaves`),
  getActiveLeaves: () => api.get('/leaves/active'),
  createLeave: (id, data) => api.post(`/employees/${id}/leaves`, data),
  updateLeave: (leaveId, data) => api.put(`/leaves/${leaveId}`, data),
  deleteLeave: (leaveId) => api.delete(`/leaves/${leaveId}`),
  getAdvances: (id) => api.get(`/employees/${id}/advances`),
  createAdvance: (id, data) => api.post(`/employees/${id}/advances`, data),
  deleteAdvance: (advanceId) => api.delete(`/advances/${advanceId}`),
  getAllAdvances: (params) => api.get('/advances', { params }),
};

export const overtimeAPI = {
  getAll: (employeeId = null) => api.get('/overtime', { params: { employee_id: employeeId } }),
  create: (data) => api.post('/overtime', data),
  update: (id, data) => api.put(`/overtime/${id}`, data),
  delete: (id) => api.delete(`/overtime/${id}`),
};

export const paymentsAPI = {
  getAll: () => api.get('/payments'),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payments/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payments/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importCommit: (rows) => api.post('/payments/import/commit', { rows }),
  getDuplicates: () => api.get('/payments/duplicates'),
  bulkDelete: (ids) => api.post('/payments/bulk-delete', { ids }),
};

export const loansAPI = {
  getAll: () => api.get('/loans'),
  create: (data) => api.post('/loans', data),
  update: (id, data) => api.put(`/loans/${id}`, data),
  delete: (id) => api.delete(`/loans/${id}`),
};

export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

export const bankStatementAPI = {
  upload: (file, accountId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = '/bank-statement/upload';
    if (accountId) {
      url += `?account_id=${accountId}`;
    }
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getEntries: (batchId, status, accountId, statementType) => {
    const params = {};
    if (batchId) params.batch_id = batchId;
    if (status) params.status = status;
    if (accountId) params.account_id = accountId;
    if (statementType) params.statement_type = statementType;
    return api.get('/bank-statement/entries', { params });
  },
  getUploads: () => api.get('/bank-statement/uploads'),
  exportMonth: (year, month) => api.get('/bank-statement/export-month', { params: { year, month }, responseType: 'blob' }),
  categorizeEntry: (entryId, categoryData) => 
    api.put(`/bank-statement/entries/${entryId}/categorize`, categoryData),
  bulkCategorize: (entryIds, category, subCategory) =>
    api.put('/bank-statement/entries/bulk-categorize', { entry_ids: entryIds, category, sub_category: subCategory }),
  deleteBatch: (batchId) => api.delete(`/bank-statement/batch/${batchId}`),
  getCategories: () => api.get('/bank-statement/categories'),
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  read: (id) => api.post(`/notifications/${id}/read`),
  readAll: () => api.post('/notifications/read-all'),
  publicKey: () => api.get('/push/public-key'),
  subscribe: (sub) => api.post('/push/subscribe', sub),
};

export const driveAPI = {
  connect: () => api.get('/drive/connect'),
  getStatus: () => api.get('/drive/status'),
  disconnect: () => api.post('/drive/disconnect'),
  backup: () => api.post('/drive/backup'),
  getBackupHistory: () => api.get('/drive/backup-history'),
  // Auto-backup settings
  getAutoBackupStatus: () => api.get('/drive/auto-backup-status'),
  updateAutoBackupSettings: (enabled, backupTime = '02:00') => 
    api.post(`/drive/auto-backup-settings?enabled=${enabled}&backup_time=${backupTime}`),
};
export const roomRentAPI = {
  getRooms: () => api.get('/room-rent/rooms'),
  createRoom: (data) => api.post('/room-rent/rooms', data),
  updateRoom: (id, data) => api.put(`/room-rent/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/room-rent/rooms/${id}`),
  getMonth: (month) => api.get('/room-rent/month', { params: { month } }),
  payHead: (data) => api.post('/room-rent/head-payments', data),
  undoHead: (id) => api.delete(`/room-rent/head-payments/${id}`),
  template: () => api.get('/room-rent/import-template', { responseType: 'blob' }),
  importFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/room-rent/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const craneDocsAPI = {
  list: () => api.get('/crane-documents'),
  expiring: () => api.get('/crane-documents/expiring'),
  save: (craneId, type, data) => api.put(`/crane-documents/${craneId}/${type}`, data),
  upload: (craneId, type, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/crane-documents/${craneId}/${type}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getFile: (path) => api.get(`/crane-documents/file/${path}`, { responseType: 'blob' }),
  remove: (craneId, type) => api.delete(`/crane-documents/${craneId}/${type}`),
};
